import React, { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import LeadBadge from '../components/ui/LeadBadge';
import { useSupabaseQuery } from '../hooks/useSupabase';
import { useRealtimeMessages } from '../hooks/useRealtimeMessages';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

interface ConvRow {
  id: string;
  contact_id: string;
  session_id: string;
  status: string;
  lead_temperature: string;
  last_message_at: string;
  contacts?: { first_name: string | null; last_name: string | null; company: string | null; phone: string | null; tags: string[] } | null;
  ai_agents?: { name: string } | null;
  last_message_body?: string;
  needs_response?: boolean;
  language?: 'ru' | 'az' | 'en';
}

const MOCK_CONVS: ConvRow[] = [
  { id: '1', contact_id: '1', session_id: '', status: 'active', lead_temperature: 'hot', last_message_at: new Date().toISOString(), contacts: { first_name: 'Sara', last_name: 'Hasanova', company: 'SoftTech', phone: null, tags: ['Warm'] }, ai_agents: { name: 'Luna' }, needs_response: true, language: 'az' },
  { id: '2', contact_id: '2', session_id: '', status: 'active', lead_temperature: 'warm', last_message_at: new Date().toISOString(), contacts: { first_name: 'Dmitry', last_name: 'Ivanov', company: 'Logistics', phone: null, tags: ['Cold'] }, ai_agents: { name: 'Atlas' }, needs_response: false, language: 'ru' },
];

function getContactName(c: ConvRow): string {
  const fn = c.contacts?.first_name ?? '';
  const ln = c.contacts?.last_name ?? '';
  return [fn, ln].filter(Boolean).join(' ') || 'Unknown';
}

export default function WarmLeads(): React.JSX.Element {
  const [selected, setSelected] = useState<ConvRow | null>(null);
  const [msgText, setMsgText] = useState('');
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: liveConvs, loading: convsLoading, refetch } = useSupabaseQuery<ConvRow[]>(
    async () => {
      if (!isSupabaseConfigured) return { data: MOCK_CONVS, error: null };
      const { data: rows, error: err } = await supabase
        .from('conversations')
        .select('id, contact_id, session_id, status, lead_temperature, last_message_at, contacts(first_name,last_name,company,phone,tags), ai_agents(name)')
        .order('last_message_at', { ascending: false });

      // Enhance with basic logic for needs_response and language (mocked for now)
      const enhanced = (rows as any[])?.map(r => ({
        ...r,
        needs_response: Math.random() > 0.5, // Simulate
        language: Math.random() > 0.5 ? 'az' : 'ru'
      }));
      return { data: enhanced as ConvRow[] | null, error: err };
    },
    []
  );

  const conversations = liveConvs ?? (isSupabaseConfigured ? [] : MOCK_CONVS);
  const filtered = conversations.filter(c => {
    const q = search.toLowerCase();
    return getContactName(c).toLowerCase().includes(q) || (c.contacts?.company ?? '').toLowerCase().includes(q);
  });

  const selectedConv = selected ?? filtered[0] ?? null;
  const { messages, loading: msgsLoading } = useRealtimeMessages(selectedConv?.id);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!msgText.trim() || !selectedConv) return;
    setSending(true);
    try {
      // Logic to send message
      toast.success('Message sent!');
      setMsgText('');
    } catch (err) {
      toast.error('Failed to send');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex gap-4 h-[calc(100vh-140px)]">
      {/* Sidebar: Conv List */}
      <div className="w-80 flex flex-col bg-dx-surface border border-dx-border rounded-2xl overflow-hidden shadow-sm">
        <div className="p-4 border-b border-dx-border bg-dx-bg/30">
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search leads..."
            className="w-full bg-dx-bg border border-dx-border rounded-xl px-3 py-2 text-[12px] text-dx-text outline-none focus:border-dx-accent"
          />
        </div>
        <div className="flex-1 overflow-auto">
          {filtered.map(c => {
            const isSelected = selectedConv?.id === c.id;
            return (
              <div
                key={c.id}
                onClick={() => setSelected(c)}
                className={`p-4 cursor-pointer border-l-2 transition-all hover:bg-dx-bg/20 ${isSelected ? 'bg-dx-accent/5 border-dx-accent' : 'border-transparent'}`}
              >
                <div className="flex justify-between items-start mb-1">
                  <span className={`text-[13px] font-bold ${isSelected ? 'text-dx-text' : 'text-dx-muted'}`}>{getContactName(c)}</span>
                  <span className="text-[10px] text-dx-dim">{new Date(c.last_message_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[11px] text-dx-dim truncate max-w-[120px]">{c.contacts?.company || 'Company'}</span>
                  <div className="flex gap-1">
                    {c.needs_response && <div className="w-2 h-2 rounded-full bg-dx-danger animate-pulse" title="Needs Response" />}
                    <span className="px-1.5 py-0.5 rounded bg-dx-bg border border-dx-border text-[9px] uppercase font-bold text-dx-dim">{c.language}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Main: Chat View */}
      <div className="flex-1 flex flex-col bg-dx-surface border border-dx-border rounded-2xl overflow-hidden shadow-lg">
        {selectedConv ? (
          <>
            <div className="px-6 py-4 border-b border-dx-border flex justify-between items-center bg-dx-bg/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-linear-to-br from-dx-accent to-dx-info flex items-center justify-center text-black font-bold text-xs ring-2 ring-dx-surface shadow-sm">
                  {getContactName(selectedConv).split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <h3 className="text-sm font-bold text-dx-text m-0">{getContactName(selectedConv)}</h3>
                  <div className="flex gap-2 mt-1">
                    <span className="text-[10px] text-dx-dim uppercase font-semibold">{selectedConv.contacts?.company}</span>
                    <span className="text-[10px] text-dx-accent uppercase font-bold px-1.5 py-0.5 bg-dx-accent/10 rounded">Language: {selectedConv.language === 'az' ? 'Azerbaijani' : 'Russian'}</span>
                  </div>
                </div>
              </div>
              <LeadBadge status={selectedConv.lead_temperature} />
            </div>

            <div className="flex-1 p-6 overflow-auto flex flex-col gap-4 bg-dx-bg/5">
              {messages.map((m, i) => {
                const isOut = m.direction === 'outbound';
                return (
                  <div key={i} className={`flex ${isOut ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] p-4 shadow-sm ${isOut ? 'bg-dx-accent text-black rounded-2xl rounded-tr-none' : 'bg-dx-surface border border-dx-border text-dx-text rounded-2xl rounded-tl-none'}`}>
                      <p className="text-[13px] m-0 leading-relaxed font-medium">{m.body}</p>
                      <p className={`text-[10px] mt-2 mb-0 ${isOut ? 'text-black/60 font-bold' : 'text-dx-dim'}`}>
                        {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        {isOut && ` · ${m.sender_type === 'ai_agent' ? '🤖 AI' : '👤 You'}`}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            <div className="p-4 border-t border-dx-border bg-dx-bg/20 flex gap-3">
              <input
                value={msgText}
                onChange={e => setMsgText(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 bg-dx-surface border border-dx-border rounded-xl px-4 py-3 text-[13px] text-dx-text outline-none focus:border-dx-accent shadow-inner"
              />
              <button
                onClick={handleSend}
                disabled={sending || !msgText.trim()}
                className="px-6 py-3 rounded-xl border-none font-bold text-black bg-[linear-gradient(135deg,#25D366,#1BA350)] disabled:opacity-50 shadow-md active:translate-y-0.5 transition-transform"
              >
                Send
              </button>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-dx-dim gap-3 grayscale opacity-40">
            <span className="text-4xl">💬</span>
            <p className="text-sm font-medium">Select a lead to start nurturing</p>
          </div>
        )}
      </div>
    </div>
  );
}
